import Button from '@components/base/button.component.tsx';
import CardActions from '@components/base/card/card-actions.component.tsx';
import CardContent from '@components/base/card/card-content.component.tsx';
import CardHeader from '@components/base/card/card-header.component.tsx';
import Card from '@components/base/card/card.component.tsx';
import { CircularLoaderContainer } from '@components/base/circular-loader-container.component.tsx';
import TextField from '@components/base/text-field.component.tsx';
import { useGlobalStore } from '@/stores';
import { otherCountries, priorityCountries } from '@utils/countries.ts';
import { createAddressSchema } from '@validation/address.schema.ts';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import {
  useDefaultShippingAddress,
  useGetAddressesByBusinessPartnerId,
} from '@/api/business-partner/business-partner.hooks.ts';
import { Address } from '@/api/business-partner/business-partner.types.ts';
import { useTranslation } from 'react-i18next';

export function CartDeliveryAddress() {
  const navigate = useNavigate();
  const { t } = useTranslation('cart');
  const {
    addressName2,
    addressName3,
    street,
    zipCode,
    city,
    country,
    setAddressName2,
    setAddressName3,
    setStreet,
    setZipCode,
    setCity,
    setCountry,
  } = useGlobalStore();

  const { data: addresses = [], isPending: isAddressesPending } =
    useGetAddressesByBusinessPartnerId();

  const { defaultShippingAddress, isPending: isDefaultShippingAddressPending } =
    useDefaultShippingAddress();

  // Create schema with translations
  const addressSchema = useMemo(
    () =>
      createAddressSchema(t).and(
        z.object({ selectedAddress: z.string().optional() }),
      ),
    [t],
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isValid },
    clearErrors,
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(addressSchema),
    defaultValues: {
      selectedAddress: 'default',
      addressName2: addressName2 || defaultShippingAddress?.addressName2 || '',
      addressName3: addressName3 || defaultShippingAddress?.addressName3 || '',
      street: street || defaultShippingAddress?.street || '',
      zipCode: zipCode || defaultShippingAddress?.zipCode || '',
      city: city || defaultShippingAddress?.city || '',
      country: country || defaultShippingAddress?.country || '',
    },
  });

  const selectedAddress = watch('selectedAddress');
  const watchedFields = watch([
    'addressName2',
    'addressName3',
    'street',
    'zipCode',
    'city',
    'country',
  ]);

  useEffect(() => {
    if (
      !addressName2 &&
      !addressName3 &&
      !street &&
      !zipCode &&
      !city &&
      !country &&
      defaultShippingAddress != null
    ) {
      setValue('selectedAddress', defaultShippingAddress?.id || '');
      setValue('addressName2', defaultShippingAddress?.addressName2 || '');
      setValue('addressName3', defaultShippingAddress?.addressName3 || '');
      setValue('street', defaultShippingAddress?.street || '');
      setValue('zipCode', defaultShippingAddress?.zipCode || '');
      setValue('city', defaultShippingAddress?.city || '');
      setValue('country', defaultShippingAddress?.country || '');
    }
  }, [defaultShippingAddress]);

  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (selectedAddress !== 'default' && addresses) {
      const selectedAddressData = addresses.find(
        (address) => address.id === selectedAddress,
      );
      if (selectedAddressData) {
        setInitialLoad(true);
        setValue('addressName2', selectedAddressData.addressName2 || '');
        setValue('addressName3', selectedAddressData.addressName3 || '');
        setValue('street', selectedAddressData.street || '');
        setValue('zipCode', selectedAddressData.zipCode || '');
        setValue('city', selectedAddressData.city || '');
        setValue('country', selectedAddressData.country || '');
        clearErrors(); // Clear all errors when a predefined address is selected
        setTimeout(() => setInitialLoad(false), 0);
      }
    }
  }, [selectedAddress, addresses, setValue, clearErrors]);

  useEffect(() => {
    if (!initialLoad && selectedAddress !== 'default') {
      const selectedAddressData = addresses?.find(
        (address) => address.id === selectedAddress,
      );
      if (selectedAddressData) {
        const isModified = watchedFields.some((field, index) => {
          const keys = [
            'addressName2',
            'addressName3',
            'street',
            'zipCode',
            'city',
            'country',
          ];
          return (
            field !== (selectedAddressData[keys[index] as keyof Address] || '')
          );
        });
        if (isModified) {
          setValue('selectedAddress', 'default');
        }
      }
    }
  }, [watchedFields, selectedAddress, addresses, setValue, initialLoad]);

  const submitHandler = (data: Partial<Address>) => {
    setAddressName2(data.addressName2 || null);
    setAddressName3(data.addressName3 || null);
    setStreet(data.street || null);
    setZipCode(data.zipCode || null);
    setCity(data.city || null);
    setCountry(data.country || null);
    navigate('/bp/c/cart/3');
  };

  const handleChangeSelect = (event: SelectChangeEvent) => {
    setValue('selectedAddress', event.target.value);
  };

  const isFormValid = isValid || selectedAddress !== 'default';

  return (
    <Card sx={{ width: '100%' }} elevation={2}>
      <form onSubmit={handleSubmit(submitHandler)}>
        <CardHeader title={t('deliveryAddress.title')} />
        {isAddressesPending || isDefaultShippingAddressPending ? (
          <CircularLoaderContainer />
        ) : (
          <CardContent sx={{ width: '100%' }}>
            <Stack spacing={4}>
              <FormControl variant="filled" sx={{ width: '100%' }}>
                <InputLabel id="address-select-label">
                  {t('deliveryAddress.myAddresses')}
                </InputLabel>
                <Controller
                  name="selectedAddress"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="address-select-label"
                      onChange={handleChangeSelect}
                    >
                      <MenuItem value="default">
                        {t('deliveryAddress.customAddress')}
                      </MenuItem>
                      {addresses
                        .filter((x) => x.addressType === 'bo_ShipTo')
                        .map((address) => (
                          <MenuItem key={address.id} value={address.id}>
                            {defaultShippingAddress?.id === address.id &&
                              `${t('deliveryAddress.default')} `}
                            {address.addressName2} - {address.street}
                            {', '}
                            {address.zipCode} {address.city}
                            {', '} {address.country}{' '}
                          </MenuItem>
                        ))}
                    </Select>
                  )}
                />
              </FormControl>
              <>
                <Controller
                  name="addressName2"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('deliveryAddress.fields.name')}
                      sx={{ width: '100%' }}
                      variant="filled"
                      error={!!errors.addressName2}
                      helperText={errors.addressName2?.message}
                    />
                  )}
                />
                <Controller
                  name="addressName3"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('deliveryAddress.fields.addressAddition')}
                      sx={{ width: '100%' }}
                      variant="filled"
                      error={!!errors.addressName3}
                      helperText={errors.addressName3?.message}
                    />
                  )}
                />
                <Controller
                  name="street"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={t('deliveryAddress.fields.street')}
                      sx={{ width: '100%' }}
                      variant="filled"
                      error={!!errors.street}
                      helperText={errors.street?.message}
                    />
                  )}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Controller
                    name="zipCode"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t('deliveryAddress.fields.postalCode')}
                        sx={{ width: '30%' }}
                        variant="filled"
                        error={!!errors.zipCode}
                        helperText={errors.zipCode?.message}
                      />
                    )}
                  />
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t('deliveryAddress.fields.city')}
                        sx={{ width: '70%' }}
                        variant="filled"
                        error={!!errors.city}
                        helperText={errors.city?.message}
                      />
                    )}
                  />
                </Box>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <FormControl
                      variant="filled"
                      sx={{ width: '100%' }}
                      error={!!errors.country}
                    >
                      <InputLabel id="country-select-label">
                        {t('deliveryAddress.fields.country')}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="country-select-label"
                        label={t('deliveryAddress.fields.country')}
                      >
                        <MenuItem value="">
                          <em>{t('deliveryAddress.fields.selectCountry')}</em>
                        </MenuItem>
                        {priorityCountries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                        <ListSubheader>
                          {t('deliveryAddress.fields.otherEuCountries')}
                        </ListSubheader>
                        {otherCountries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.country && (
                        <FormHelperText>
                          {errors.country.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </>
            </Stack>
          </CardContent>
        )}
        <CardActions>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/bp/c/cart')}
          >
            {t('deliveryAddress.backToCart')}
          </Button>
          <Button
            type="submit"
            endIcon={<ArrowForward />}
            variant="contained"
            disabled={
              !isFormValid ||
              isDefaultShippingAddressPending ||
              isAddressesPending
            }
          >
            {isDefaultShippingAddressPending || isAddressesPending
              ? t('actions.loading')
              : t('checkout.continue')}
          </Button>
        </CardActions>
      </form>
    </Card>
  );
}
